/**
 * Shared mapping helpers for every device rendered on a floor plan.
 * They deliberately keep API field names at this boundary so UI components do
 * not need to know a vendor-specific payload.
 */
export const buildPosition = (floorDevice) => ({
  x: floorDevice.x,
  y: floorDevice.y,
  width: floorDevice.width,
  height: floorDevice.height,
  degree: floorDevice.degree,
  cone: {
    visible: !!floorDevice.cone_visible,
    distance: floorDevice.cone_distance ? parseFloat(floorDevice.cone_distance) : 80,
    angle: floorDevice.cone_angle ? parseFloat(floorDevice.cone_angle) : 45,
  },
})

export const inferPanelDeviceType = (panelType, proxyType) => {
  if (panelType && panelType !== 'panel') return panelType
  if (!proxyType) return panelType

  const match = proxyType.match(/alarm_bridge_(.+)/)
  return match ? match[1] : panelType
}

export const resolvePanelProxyId = (floorDevice, planeDevices) => {
  const explicitPanelProxyId = floorDevice.device?.panel_proxy_id
  if (explicitPanelProxyId) return explicitPanelProxyId

  if (floorDevice.device?.device_panel_type !== 'area') return null

  return planeDevices.find((item) => item.device?.device_type === 'alarm_bridge')
    ?.id_camera_proxy ?? null
}

export const createBaseDevice = (floorDevice, context) => {
  const proxy = floorDevice.device ?? {}
  const proxyType = proxy.type ?? null
  const router = proxy.router ?? null
  const isRouter = Boolean(router)
  const cameraAlarm = context.relations.cameraAlarmStates?.[floorDevice.id_camera_proxy] ?? null

  return {
    id: floorDevice.id_generic ?? floorDevice.id_camera_proxy,
    name: router?.imei ?? proxy.name ?? `Device ${floorDevice.id_camera_proxy}`,
    vendor: 'generic',
    kind: isRouter ? 'router' : (proxy.device_type ?? 'device'),
    id_camera_proxy: floorDevice.id_camera_proxy,
    floor_plant_id: context.planeId,
    maintenance: floorDevice.maintenance ?? null,
    id_generic: floorDevice.id_generic ?? null,
    camera_id: proxy.camera_id ?? null,
    device_type: isRouter ? 'router' : (proxy.device_type ?? null),
    camera_type: isRouter ? 'router' : proxyType,
    // TODO: Render the live router connectivity status when the API provides it.
    status: isRouter ? (router.status ?? 'Online') : (proxy.related_device?.status_text ?? proxy.status ?? null),
    is_router: isRouter,
    is_primary: router?.is_primary ?? false,
    bypass_text: proxy.related_device?.bypass_text ?? null,
    panel_device_type: inferPanelDeviceType(proxy.device_panel_type ?? null, proxyType),
    panel_proxy_id: resolvePanelProxyId(floorDevice, context.planeDevices),
    proxy_type: proxy.device_type ?? null,
    proxy_camera_type: proxyType,
    source_id: proxy.related_device?.source_id ?? proxy.source_id ?? null,
    device_tag: proxy.device_tag ?? null,
    icon: null,
    color: null,
    color_icon: null,
    text_color: null,
    acronym: null,
    value: null,
    actions: null,
    can_update_alarm: false,
    dispatch_url: null,
    cancel_url: null,
    showAlarm: cameraAlarm?.active === true,
    active_alarm: false,
    virtual_bypass: false,
    alarmColor: cameraAlarm?.alarm_color ?? '',
    alarmIcon: null,
    latest_alarm: null,
    position: buildPosition(floorDevice),
  }
}
